import { useCallback } from 'react';
import {
  UserRole,
  esAccesoTotal,
  tienePermiso,
  type AccionAdmin,
  type ModuloAdmin,
} from '@kick/types';
import { useAuth } from './auth-store';

/**
 * Qué módulo de permisos gobierna cada ruta de administración.
 *
 * El menú y las rutas se filtran con esto. «Flota» y «Domiciliarios» son el
 * mismo módulo; «Calificaciones» (las peores) e «Informes» también.
 */
export const MODULO_POR_RUTA: Record<string, ModuloAdmin> = {
  '/operacion': 'operacion',
  '/programadas': 'programadas',
  '/manuales': 'manuales',
  '/comercios': 'comercios',
  '/flota': 'domiciliarios',
  '/domiciliarios': 'domiciliarios',
  '/usuarios-plataforma': 'usuarios',
  '/zonas': 'zonas',
  '/calificaciones': 'informes',
  '/clasificacion': 'clasificacion',
  '/cupones': 'cupones',
  '/informes-plataforma': 'informes',
  '/cobros': 'cobros',
  '/liquidaciones': 'liquidaciones',
  '/anuncios': 'anuncios',
  '/soporte': 'soporte',
  '/reclamos': 'reclamos',
  '/auditoria': 'auditoria',
  '/ajustes': 'ajustes',
};

/**
 * ¿Puede este usuario hacer esto?
 *
 * Para un comercio siempre es `true`: sus límites los ponen su rol y su
 * alcance, no este sistema. Para un administrador con acceso total también.
 * Solo un administrador con permisos detallados ve cosas apagadas.
 *
 * Es comodidad de interfaz —esconder el botón que no va a funcionar—; la API
 * vuelve a comprobar cada permiso en cada petición.
 */
export function usePuede(): (modulo: ModuloAdmin, accion: AccionAdmin) => boolean {
  const user = useAuth((s) => s.user);

  return useCallback(
    (modulo: ModuloAdmin, accion: AccionAdmin) => {
      if (!user || user.role !== UserRole.ADMIN) return true;
      return tienePermiso(user.adminPermissions, modulo, accion);
    },
    [user],
  );
}

/** Acceso total: el único que gestiona el equipo de administración. */
export function useAccesoTotal(): boolean {
  const user = useAuth((s) => s.user);
  return user?.role === UserRole.ADMIN && esAccesoTotal(user.adminPermissions);
}
